import React, { Component } from 'react';
import { connect } from 'react-redux';
import i18next from 'i18next';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Breakpoints from 'lib/Breakpoints';
import { parsePersonNames } from 'lib/parsePersonNames';
import { loadPersonsAction } from '../../../redux/modules/person';
import { AuthorCard } from '../../Card/Author';
import ButtonHoverAnimation from '../../ButtonHoverAnimation';
import { content as ContentPropType, person as PersonPropType } from '../../../lib/CustomPropTypes';

class Hub extends Component {
  static propTypes = {
    content: ContentPropType.isRequired,
    dispatch: PropTypes.func.isRequired,
    error: PropTypes.bool,
    loading: PropTypes.bool,
    persons: PropTypes.arrayOf(PersonPropType),
  }

  static defaultProps = {
    error: false,
    loading: false,
    persons: [],
  }

  constructor(props) {
    super(props);
    this.state = {
      columnCount: 4,
      displayAllRows: false,
    };
  }

  componentDidMount() {
    // Fetch persons to be displayed
    const { dispatch } = this.props;
    dispatch(loadPersonsAction());

    // Change column number relative to the window size
    Breakpoints.getSmallMQL().addEventListener('change', this.handleResize);
    Breakpoints.getLargeMQL().addEventListener('change', this.handleResize);

    const currentColumnCount = this.setColumnsByBreakpoint();
    const { columnCount: prevColumnCount } = this.state;
    if (currentColumnCount !== prevColumnCount) {
      this.setState({
        columnCount: currentColumnCount,
      });
    }
  }

  componentWillUnmount() {
    Breakpoints.getSmallMQL().removeEventListener('change', this.handleResize);
    Breakpoints.getLargeMQL().removeEventListener('change', this.handleResize);
  }

  handleResize = () => {
    const columns = this.setColumnsByBreakpoint();

    this.setState({
      columnCount: columns,
    });
  }

  setColumnsByBreakpoint = () => {
    let numColumns;
    if (Breakpoints.isS()) {
      numColumns = 2;
    }
    if (Breakpoints.isM()) {
      numColumns = 3;
    }
    if (Breakpoints.isL()) {
      numColumns = 4;
    }
    if (Breakpoints.isXL()) {
      numColumns = 5;
    }
    return numColumns;
  }

  handleAuthorEntities = () => {
    const { persons } = this.props;
    return persons
      .filter((person) => person?.url?.primary?.length > 1)
      .map((author) => parsePersonNames(author))
      .sort((a, b) => (
        // eslint-disable-next-line max-len,no-nested-ternary
        (a.name.last > b.name.last) ? 1 : (a.name.last === b.name.last) ? ((a.name.first > b.name.first) ? 1 : -1) : -1));
  }

  lastShownRow = (index, numberOfAuthors) => {
    const { columnCount, displayAllRows } = this.state;
    const numberOfItemsOnLastRow = numberOfAuthors % columnCount;

    let lastShownRow = false;

    if (displayAllRows) {
      if (numberOfItemsOnLastRow === 0 && numberOfAuthors - index <= columnCount) {
        lastShownRow = true;
      }

      if (numberOfItemsOnLastRow > 0 && numberOfAuthors - index <= numberOfItemsOnLastRow) {
        lastShownRow = true;
      }
    }

    if (!displayAllRows) {
      if (columnCount === 2 && (index === 6 || index === 7)) {
        lastShownRow = true;
      }

      if (columnCount > 2 && index > columnCount - 1 && index <= columnCount * 2 - 1) {
        lastShownRow = true;
      }
    }

    return lastShownRow;
  };

  render() {
    const { content: { metadata }, error, loading } = this.props;

    if (error) return null;

    if (loading) return <section className="entity-hub" />;

    const { columnCount, displayAllRows } = this.state;
    const entityType = get(metadata, 'entityType', 'author');
    let authors = [];
    let collapseButtonText;
    let expandButtonText;
    let title;

    if (entityType === 'author') {
      authors = this.handleAuthorEntities();
      collapseButtonText = get(metadata, 'collapseButtonText', i18next.t('SHOW FEWER COLUMNISTS'));
      expandButtonText = get(metadata, 'expandButtonText', i18next.t('SHOW MORE COLUMNISTS'));
      title = get(metadata, 'title', i18next.t('Columnists'));
    }

    return (
      <section className="entity-hub">
        <div className="entity-hub__wrapper">
          <title className="entity-hub__title">{title}</title>
          <div className="entity-hub__header-border" />
          <div
            className={
              classNames(
                'entity-hub__entities',
                { 'entity-hub__entities--show-all': displayAllRows },
                { 'entity-hub__entities--show-two-rows': !displayAllRows && columnCount > 2 },
                { 'entity-hub__entities--show-four-rows': !displayAllRows && columnCount === 2 },
              )
            }
          >
            {
              authors.map((author, index) => (
                <div
                  className={
                    classNames(
                      { 'entity-hub__entity--no-bottom-border': this.lastShownRow(index, authors.length) },
                    )
                  }
                >
                  <AuthorCard author={author} key={author.id} />
                </div>
              ))
            }
          </div>
          <ButtonHoverAnimation
            title={displayAllRows ? collapseButtonText : expandButtonText}
            onClick={() => this.setState((prev) => ({ displayAllRows: !prev.displayAllRows }))}
            type="buttonWithArrow"
            additionalClasses="entity-hub__action-button"
            iconStyle={
              displayAllRows
                ? 'button-hover-animation__button-icon icon icon-angle-down up'
                : 'button-hover-animation__button-icon icon icon-angle-down'
            }
          />
        </div>
      </section>
    );
  }
}

const mapStateToProps = (state) => ({
  persons: state.person.items,
  loading: state.person.loading,
  error: state.person.error,
});

export const EntityHub = connect(mapStateToProps)(Hub);
